import { lockBoss } from './lockon';
import { camKick, camPunch } from './camera';
import { RULE } from './rules';
import { toggleLock, lockedTarget, updateLock } from './lockon';
import { turnTurret } from './turret';
import { damp, rnd } from '../core/math';
import { FLY, airborne, dashCost, updateFlight } from './flight';
import { RNG } from '../core/rng';
import { enemies } from '../entities/enemies';
import { mechEvent } from '../entities/mech-anim';
import { SFX } from '../platform/audio';
import { elStage } from '../platform/dom';
import { CAM_OFF, camera } from '../render/scene';
import { STATE, angDiff, areaDamage, damageEnemy, hitstop, nearestEnemy, shake, spawnDebris, spawnRing, tmpV } from './fx';
import { metTaken } from './metrics';
import { WEAPONS, dashSlashFinish, VORTEX_SPD } from './weapons';
/* `weaponById` **一定要拿 `loadout` 的那一支**，不是 `data/weapons-pool` 的同名函式。
   池子那支只查 `WEAPON_POOL` 的 10 把，查不到進化型與融合型 —— 而下面的開火迴圈
   查不到定義就 `continue`。結果是**每一個進化與融合都一發都不開**：
   玩家拿到進化的那一秒，那個槽位直接變成 0 輸出（進化還會取代來源武裝，所以是淨損失）。
   沒有任何錯誤訊息，`take()` 也回報成功，槽位上圖示、名稱、說明全部正常。
   2026-08-05 由 `pnpm crafts` 量出來（六個質變的「之後 DPS」全部是 0）。 */
import { weapons as loadWeapons, weaponById } from './loadout';
import { banner, gameOver } from '../ui/hud';
import { STEP, VAULT, clampArena, resolveCircle } from '../world/arena';
import { P, slashFx, aimSlashFx } from '../world/player';
/* ============================================================
   玩家
   ============================================================ */
export var DASH_TIME = 0.17, DASH_SPD = 46;
export function updatePlayer(dt, inp) {
  // 保持 ax 的 {x,z,len} 形狀，下面四處用法一行都不用改
  var ax = { x: inp.moveX, z: inp.moveZ, len: Math.hypot(inp.moveX, inp.moveZ) }, rig = P.rig;

  /* **衝刺與飛行共用同一條能量**（2026-08-06 使用者拍板）。
     在這之前衝刺是「格數＋2.6 秒回充」的獨立資源，跟推進能量毫無交互 ——
     兩條各自回滿的資源等於沒有取捨。現在一次衝刺吃 25% 上限，
     而起飛門檻是 65%：衝一次還飛得起來，衝兩次就飛不了。

     回充延遲沿用飛行的 `regenDelay` —— 衝完立刻回滿就等於沒有成本。 */
  var dcost = dashCost();
  if (inp.dash && P.dashT <= 0 && P.thrust >= dcost) {
    P.thrust = Math.max(0, P.thrust - dcost);
    P.thrustCd = FLY.regenDelay;
    P.dashT = DASH_TIME; P.dashAttack = false;
    /* 殘影：把衝刺的**起點**記下來，衝刺結束時在那裡爆炸。
       記起點而不是終點，是因為起點正是「你剛剛被包圍的地方」。 */
    if (RULE.afterimage > 0) { P.ghostX = P.pos.x; P.ghostZ = P.pos.z; P.ghostT = 0.35; }
    var a = ax.len > 0 ? Math.atan2(ax.x, ax.z) : P.faceAim;
    P.dashDir.set(Math.sin(a), 0, Math.cos(a));
    P.iframe = Math.max(P.iframe, DASH_TIME + 0.12);
    SFX.dash(); shake(0.14);
    /* 衝刺推鏡：FOV 推開 + 跟隨變鈍。**位移本身不會給速度感，鏡頭跟不上才會。** */
    camKick(1);
    spawnRing(P.pos.x, P.pos.z, 0x5fe3ff, 0.8, 5, 0.3);
  }
  inp.dash = false;

  if (P.dashT > 0) {
    P.dashT -= dt;
    var mul = P.dashAttack ? 62 : DASH_SPD;
    P.vel.copy(P.dashDir).multiplyScalar(mul * (0.4 + P.dashT / (P.dashAttack ? 0.24 : DASH_TIME)));
    if (Math.random() < 0.9) spawnDebris(P.pos.x, 1.4, P.pos.z, P.dashAttack ? 0x9fffe8 : 0x5fe3ff, 1, 0.15, 0.2);   // det-ok: 純視覺：衝刺殘骸的生成機率
    if (P.dashAttack) {
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        if (P.slashHits.indexOf(e) >= 0) continue;
        var d0 = Math.hypot(e.pos.x - P.pos.x, e.pos.z - P.pos.z);
        if (d0 < 3.2 + e.r) {
          P.slashHits.push(e);
          var cr = RNG.next() < P.s.crit;
          /* **途中只結算四成**，剩下六成留給收刀那一拍（居合，
             `.claude/notes/lancer2-arts.md` §2）。完全延後的話衝刺途中
             毫無回饋，玩家會以為技能沒生效。 */
          damageEnemy(e, P.s.midDmg * 0.40 * (cr ? P.s.critMul : 1), cr, P.dashDir.x * 10, P.dashDir.z * 10, 'dashSlash', undefined, undefined, 'mech-mid');
        }
      }
      /* 收刀：衝刺結束的那一瞬間，對這趟砍中的每一隻一起兌現。
         **要排在 `after` 模組之前** —— 收刀是本體，殘影連斬是加在它上面的。 */
      if (P.dashT <= 0) dashSlashFinish(P.slashHits);
      if (P.dashT <= 0 && P.mod.mid === 'after') {
        areaDamage(P.pos.x, P.pos.z, 6.5, P.s.midDmg * 0.8, 0x9fffe8, undefined, 'mech-mid');
        slashFx.visible = true; slashFx.userData.life = 0;
        slashFx.position.set(P.pos.x, 1.6, P.pos.z);
        aimSlashFx(P.faceAim); slashFx.scale.set(6.5, 1, 6.5);
        SFX.slash(); shake(0.25);
      }
    }
  } else {
    P.dashAttack = false;
    /* 空中稍快但轉向遲鈍（damp 11 → 5）。**慣性就是操作感的來源** ——
       降落點要提前預判，飛行才是一個決定而不是一顆「更好的移動鍵」。 */
    var air = airborne();
    /* 旋刃期間加速：近戰的大招要**追得到人**，不然它是一盞路燈
       （`.claude/notes/lancer2-arts.md` §3）。乘在移動速度而不是 `P.s.speed` ——
       它是這四秒的臨時狀態，不該進修正層（進去就會被別的系統讀到）。 */
    /* **`P.ultT` 是兩台機體共用的欄位**（CHASSIS-0 的光束大招也用它），
       所以一定要再問一次「大招是不是旋刃」。只看 `ultT >= 0` 的那一版
       等於幫射擊型的光束也加速 35% —— 黃金雜湊、節奏、Boss 時序
       十條測試一起紅，而畫面上完全看不出來。 */
    var vortexOn = P.ultT >= 0 && P.mech && P.mech.ult === 'vortex';
    var sp = P.s.speed * (air ? FLY.airSpdMul : 1) * (vortexOn ? VORTEX_SPD : 1);
    var tn = air ? FLY.airTurn : FLY.groundTurn;
    P.vel.x = damp(P.vel.x, ax.x * sp, tn, dt);
    P.vel.z = damp(P.vel.z, ax.z * sp, tn, dt);
  }
  updateFlight(dt, inp);

  P.pos.x += P.vel.x * dt; P.pos.z += P.vel.z * dt;
  /* **衝刺翻得過矮掩體、跑步會被擋**（地圖決定 2）。同一個判定吃不同的
     爬升能力：走路 `STEP`（1.3，只是台階）、衝刺 `VAULT`（3.4，一塊掩體的高度）。
     翻越的「上去」不用另外寫 —— 掩體頂面本來就站得住，`updateFlight`
     看到腳下的地變高就把人抬上去，過了另一邊自然掉下來。 */
  var rc = resolveCircle(P.pos.x, P.pos.z, 1.25, P.y, P.dashT > 0 ? VAULT : STEP);
  P.pos.x = rc.x; P.pos.z = rc.z;
  var ca = clampArena(P.pos.x, P.pos.z, 2.5);
  P.pos.x = ca.x; P.pos.z = ca.z;
  rig.root.position.set(P.pos.x, P.y, P.pos.z);

  if (ax.len > 0.1) P.faceMove += angDiff(Math.atan2(ax.x, ax.z), P.faceMove) * Math.min(1, dt * 12);
  if (inp.lockBoss) { lockBoss(); inp.lockBoss = false; }
  if (inp.lock) { toggleLock(); inp.lock = false; }
  updateLock();
  /* 手動鎖定優先。沒有鎖定時行為與原本完全相同（自動打最近的）——
     鎖定存在的理由是衝擊力要集中火力才填得滿，見 sim/impact.ts。 */
  var tgt = lockedTarget() || nearestEnemy(P.pos, P.s.atkRange * 1.25 + 2, P.faceAim, true);
  // damp 給形狀，硬上限給天花板 —— 背後的敵人要等砲塔轉 0.82 秒才鎖得到
  var wantTurn = angDiff(tgt ? Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z) : P.faceMove, P.faceAim) * Math.min(1, dt * 11);
  P.faceAim += turnTurret(wantTurn, dt);
  /* **機體動畫已經整個搬到渲染層**（`entities/mech-anim.ts`，由 `game.ts`
     每個畫面幀呼叫）。理由有兩個：

     一、新的動畫系統靠八個彈簧做平滑，跑在固定模擬步上會被畫面更新率
         切成階梯；跑在畫面幀上才是它設計的樣子。
     二、模擬層不該知道 rig 長什麼樣子。舊版把「腰高該讀 waistY 還是
         寫死 2.25」這種渲染細節放在這裡，害過一次上下半身分離。

     這裡只留下模擬層真正擁有的東西：位置、朝向、冷卻。 */

  var W = WEAPONS;
  P.fireCd -= dt; P.midCd -= dt; P.ultCd -= dt;
  var pw = W[P.mech.primary];
  if (P.fireCd <= 0 && tgt && STATE.phase !== 'clear') { pw.fire(); P.fireCd = 1 / P.s.atkRate; }
  if (inp.mid && P.midCd <= 0 && STATE.phase !== 'clear') { W[P.mech.mid].fire(); P.midCd = P.s.midCdMax; }
  if (inp.ult && P.ultCd <= 0 && P.ultT < 0 && STATE.phase !== 'clear') { W[P.mech.ult].fire(); P.ultCd = P.s.ultCdMax; }
  inp.mid = false; inp.ult = false;
  var uw = W[P.mech.ult]; if (uw.update) uw.update(dt);

  /* 池子武裝：每把有自己的冷卻，全部自動開火。
     機體那三把走上面既有的路徑，不在這裡 —— 它們不佔槽位（GDD §7.1）。 */
  for (var ui = 0; ui < loadWeapons.length; ui++) {
    var us = loadWeapons[ui], ud = weaponById(us.id);
    if (ud && ud.update) ud.update(us.lv, dt, us);   // 持續型：肅清階段也要更新（收尾動畫）
  }
  if (STATE.phase !== 'clear') {
    for (var wi = 0; wi < loadWeapons.length; wi++) {
      var slot = loadWeapons[wi];
      slot.cd -= dt;
      if (slot.cd > 0) continue;
      var def = weaponById(slot.id); if (!def) continue;
      if (!def.fire || !def.cd) continue;          // 持續型沒有 fire，在下面更新
      def.fire(slot.lv);
      slot.cd = def.cd(slot.lv);
    }
  }

  /* 殘影引爆 */
  if (P.ghostT > 0) {
    P.ghostT -= dt;
    if (P.ghostT <= 0) {
      areaDamage(P.ghostX, P.ghostZ, 6.2 * (P.s.area || 1), RULE.afterimage, 0x5fe3ff, undefined, 'p-afterimage');
      spawnRing(P.ghostX, P.ghostZ, 0x5fe3ff, 1, 7, 0.32);
    }
  }
  /* 磁軌校準：**靜止**夠久才生效，一移動就歸零。
     這個遊戲的核心壓力來自貼身，所以「站住不動」的代價是玩家自己承擔的 ——
     那就是這條規則的取捨，不需要再額外加懲罰。 */
  P.stillT = (Math.hypot(P.vel.x, P.vel.z) < 1.2 && P.dashT <= 0) ? P.stillT + dt : 0;

  P.iframe -= dt; P.shTimer -= dt;
  /* 連段窗口。**沒有這一行的話窗口永遠不會過期**，連段變成「無論隔多久
     都接得下去」—— 那就等於沒有連段，只是每三刀有一刀比較重。 */
  if (P.comboT > 0) P.comboT -= dt;
  /* 護盾回充要**脫離接觸**：身邊 9 單位內還有雜兵就不回。

     原本只看「3.2 秒沒被打」，而壓力診斷量到那條件太好滿足 ——
     一場總受傷 1410、總回復 1270，**淨值只有 140**。
     削掉的血幾乎全部自己長回來，所以受傷從來不留痕跡，
     那正是「無腦刷」的感覺來源。

     改成這樣之後護盾從被動掛件變成一條玩家學得會的規則：
     想回盾就得殺出去或拉開，而那是一個真的決策。 */
  if (P.shTimer <= 0 && STATE.nearP === 0 && P.sh < P.shMax) {
    P.sh = Math.min(P.shMax, P.sh + 11 * dt);
  }
  /* 結構回復（被動「奈米修復」）。**與護盾不同，它不要求脫離接觸** ——
     護盾是「拉開就回」的戰術資源，結構是「整場慢慢長」的策略資源，
     兩個都綁同一個條件的話後者就只是前者的複製品。 */
  if (P.s.regen > 0 && P.hp < P.hpMax && P.alive) P.hp = Math.min(P.hpMax, P.hp + P.s.regen * dt);
}

/* `updateCamera` 在 M13 搬到 `sim/camera.ts` —— 它從三行 damp 長成了
   一整套規則（密度拉遠、速度感、重量、運鏡）。 */

/** `reach` = 這一招**打得到多高**（世界單位）。省略＝不吃高度（子彈、光束）。

    近戰與地面爆炸要傳自己的觸及高度，玩家飛在它上面就打不到 ——
    這是「離地免近戰」的唯一實作點。**不要改成統一的 `airborne()` 判斷**：
    自爆蟲的球狀爆炸打得到 3 單位、Boss 的落點爆炸打得到十幾單位，
    一刀切會讓 Boss 變成只要飛起來就免疫。 */
export function hurtPlayer(dmg, reach?) {
  if (reach !== undefined && P.y > reach) return;
  if (P.iframe > 0 || !P.alive || STATE.phase === 'clear' || STATE.over) return;
  /* 裝甲是**乘算**不是減法：滿級也到不了 0，所以「疊到無敵」這條路不存在。
     減法型的減傷詞條疊到 100% 就結束了，那是這類設計最典型的死法。 */
  dmg *= (P.s.armor === undefined ? 1 : P.s.armor);
  /* 威脅記帳與 iframe 都排在無敵之前（`P.god`，除錯主控台與節奏探針用，
     預設 false）。**這個順序是刻意的**：節奏探針要跑滿 20 分鐘只能開無敵，
     而 01 要的「17 分鐘威脅反超」是問「不無敵的話每秒會被打掉多少」——
     記帳排在無敵之後就永遠是 0。iframe 一起排在前面的理由是它才是
     入傷的真正上限（0.28 秒一次 ≈ 每秒 3.6 下），跳過它量到的數字沒有意義。 */
  metTaken(dmg);
  P.shTimer = 3.2; P.iframe = 0.28;
  if (P.god) return;
  if (P.sh > 0) { var a = Math.min(P.sh, dmg); P.sh -= a; dmg -= a; }
  if (dmg > 0) P.hp -= dmg;
  /* 過載排氣：**受傷本身變成一次輸出**。放在扣血之後、死亡判定之前 ——
     放在死亡判定之後的話「被打死的那一下」不會排氣，而那正是最想要它的時候。 */
  if (RULE.ventDmg > 0) {
    areaDamage(P.pos.x, P.pos.z, 8.5 * (P.s.area || 1), RULE.ventDmg, 0xffb454, undefined, 'p-vent');
    spawnRing(P.pos.x, P.pos.z, 0xffb454, 1, 9, 0.35);
  }
  mechEvent(P.rig, 'hurt');
  SFX.hurt(); shake(0.5); hitstop(0.5); camPunch(0.5);
  elStage.classList.remove('hurt'); void elStage.offsetWidth; elStage.classList.add('hurt');
  if (P.hp <= 0) {
    if (P.revives > 0) doRevive();
    else { P.hp = 0; gameOver(false); }
  }
}
export function doRevive() {
  P.revives--;
  P.hp = Math.round(P.hpMax * 0.6); P.sh = P.shMax; P.iframe = 2.6;
  mechEvent(P.rig, 'revive');
  spawnRing(P.pos.x, P.pos.z, 0xffffff, 1, 26, 0.9);
  for (var i = enemies.length - 1; i >= 0; i--) {
    if (i >= enemies.length) i = enemies.length - 1;
    if (i < 0) break;
    var e = enemies[i]; if (!e) continue; if (!e) continue;                 // damageEnemy 可能連鎖移除多隻
    var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.max(0.6, Math.hypot(dx, dz));
    if (d < 20) { e.vel.x += dx / d * 40; e.vel.z += dz / d * 40; if (!e.T.boss) damageEnemy(e, 140, false, 0, 0, undefined, undefined, undefined, 'revive'); }
  }
  SFX.revive(); shake(1.3); hitstop(0.25); camPunch(1.2);
  banner('緊急重啟 · 剩餘 ' + P.revives);
}
